import type {
  ExperimentPlan,
  PaperPlan,
  ProjectState,
  RequirementPatch,
  ResearchProfile,
} from "./types";

const profileKeys: Array<keyof ResearchProfile> = [
  "domain",
  "experience",
  "strengths",
  "priorAssets",
  "avoidTopics",
  "compute",
  "modelAccess",
  "dataAccess",
  "budget",
  "deadline",
  "riskTolerance",
  "preferredContributions",
];

const experimentKeys: Array<keyof ExperimentPlan> = [
  "hypothesis",
  "taskDefinition",
  "implementationStack",
  "runMatrix",
  "datasets",
  "evaluationProtocol",
  "primaryMetric",
  "secondaryMetrics",
  "baselines",
  "mainComparison",
  "ablations",
  "hyperparameters",
  "robustness",
  "caseStudy",
  "seeds",
  "computeBudget",
  "artifactPlan",
  "reportingPlan",
  "stopCriteria",
  "pivotCriteria",
  "requiredEvidence",
];

const paperKeys: Array<keyof PaperPlan> = [
  "workingTitle",
  "centralClaim",
  "contributions",
  "narrative",
  "sections",
  "citationRules",
  "figureSkill",
  "reviewRounds",
  "writingConstraints",
];

function hasValue(value: unknown) {
  if (typeof value === "string") return Boolean(value.trim());
  if (Array.isArray(value)) return value.length > 0;
  return value !== undefined && value !== null;
}

function cleanValue(value: unknown) {
  if (typeof value === "string") return value.trim();
  if (Array.isArray(value)) {
    return value
      .filter((item): item is string => typeof item === "string")
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return value;
}

function mergeAllowed<T extends object>(
  current: T,
  patch: Partial<T> | undefined,
  keys: Array<keyof T>,
  prefix: string,
  onlyBlank: boolean,
  applied: string[],
  skipped: string[],
) {
  const next = { ...current };
  if (!patch || typeof patch !== "object") return next;
  for (const key of keys) {
    const incoming = cleanValue(patch[key]);
    if (!hasValue(incoming)) continue;
    const path = `${prefix}.${String(key)}`;
    if (onlyBlank && hasValue(current[key])) {
      skipped.push(path);
      continue;
    }
    next[key] = incoming as T[typeof key];
    applied.push(path);
  }
  return next;
}

export function applyRequirementPatch(
  state: ProjectState,
  patch: RequirementPatch,
  onlyBlank: boolean,
) {
  const applied: string[] = [];
  const skipped: string[] = [];
  const next = { ...state };
  const topLevelKeys: Array<
    "projectName" | "idea" | "venueUrls" | "venueManualText"
  > = ["projectName", "idea", "venueUrls", "venueManualText"];

  for (const key of topLevelKeys) {
    const incoming = cleanValue(patch[key]);
    if (!hasValue(incoming)) continue;
    if (onlyBlank && hasValue(state[key])) {
      skipped.push(key);
      continue;
    }
    next[key] = incoming as string;
    applied.push(key);
  }

  next.profile = mergeAllowed(
    state.profile,
    patch.profile,
    profileKeys,
    "profile",
    onlyBlank,
    applied,
    skipped,
  );
  next.experiment = mergeAllowed(
    state.experiment,
    patch.experiment,
    experimentKeys,
    "experiment",
    onlyBlank,
    applied,
    skipped,
  );
  next.paper = mergeAllowed(
    state.paper,
    patch.paper,
    paperKeys,
    "paper",
    onlyBlank,
    applied,
    skipped,
  );

  return { state: next, applied, skipped };
}
